import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Modal } from 'antd'
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import BrandButton from '@/components/BrandButton'
import { contentLangLabel } from '../languages'

const MAX_LEN = 1000

/** 更新日志多语种弹窗（Release Notes Textarea） */
export default function MultiLangModal({
  open,
  value,
  supported_languages,
  default_language,
  onOk,
  onCancel,
}: {
  open: boolean
  value: Record<string, string>
  supported_languages: string[]
  default_language: string
  onOk: (next: Record<string, string>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [active, setActive] = useState(default_language)
  const tabsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    setDraft({ ...value })
    setActive(
      supported_languages.includes(default_language)
        ? default_language
        : (supported_languages[0] ?? default_language),
    )
  }, [open, value, default_language, supported_languages])

  const lang = supported_languages.includes(active)
    ? active
    : (supported_languages[0] ?? default_language)

  const scrollTabs = (dir: -1 | 1) => {
    tabsRef.current?.scrollBy({ left: dir * 140, behavior: 'smooth' })
  }

  return (
    <Modal
      open={open}
      width={800}
      footer={null}
      closable={false}
      title={null}
      onCancel={onCancel}
      styles={{
        container: {
          padding: 0,
          background: '#fff',
          borderRadius: 14,
          border: '1px solid #DCDCDC',
          overflow: 'hidden',
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                flex: 1,
                padding: '10px 28px',
                fontSize: 16,
                lineHeight: '24px',
                fontWeight: 500,
                color: '#000',
              }}
            >
              {t('versionForm.releaseNotesTextarea')}
            </div>
            <div style={{ padding: '0 20px 0 10px' }}>
              <button type="button" onClick={onCancel} style={iconBtn} aria-label="close">
                <CloseOutlined style={{ fontSize: 12 }} />
              </button>
            </div>
          </div>
          <div style={{ height: 1, background: 'rgba(0,0,0,0.04)' }} />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '0 20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', minHeight: 38, gap: 8 }}>
            <div
              ref={tabsRef}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                overflow: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              {supported_languages.map((code) => {
                const on = code === lang
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setActive(code)}
                    style={{
                      flexShrink: 0,
                      padding: '8px 16px',
                      borderRadius: 12,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      lineHeight: '22px',
                      fontWeight: on ? 500 : 400,
                      color: on ? '#6366F1' : 'rgba(0,0,0,0.4)',
                      background: on ? 'rgba(99,102,241,0.1)' : 'transparent',
                    }}
                  >
                    {contentLangLabel(code)}
                  </button>
                )
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button type="button" style={arrowBtn} onClick={() => scrollTabs(-1)} aria-label="prev">
                <LeftOutlined style={{ fontSize: 10 }} />
              </button>
              <button type="button" style={arrowBtn} onClick={() => scrollTabs(1)} aria-label="next">
                <RightOutlined style={{ fontSize: 10 }} />
              </button>
            </div>
          </div>

          <Input.TextArea
            value={draft[lang] ?? ''}
            maxLength={MAX_LEN}
            placeholder={t('versionForm.textPlaceholder')}
            onChange={(e) =>
              setDraft((d) => ({ ...d, [lang]: e.target.value }))
            }
            style={{
              height: 200,
              resize: 'none',
              borderRadius: 10,
              borderColor: '#DCDCDC',
              background: '#fff',
              padding: '14px 12px',
              fontSize: 14,
              lineHeight: '22px',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <BrandButton variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </BrandButton>
            <BrandButton onClick={() => onOk(draft)}>
              {t('versionForm.confirmContent')}
            </BrandButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

const iconBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  width: 16,
  height: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#000',
}

const arrowBtn: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid #DCDCDC',
  background: '#fff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  color: '#000',
}
